import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { TransporterPackage } from '@otw/models/transporter'
import { Vehicle } from '@otw/models'
import { capResults } from 'hooks/search/filterSearchItems'
import {
  fetchMasterInventory,
  fetchTransporterPackages,
  SEARCH_CACHE_STALE_MS,
  SEARCH_QUERY_KEYS,
} from 'hooks/search/searchCaches'

interface UseTransporterPackageSearchOptions {
  searchTerm: string
  enabled?: boolean
}

export interface TransporterPackageSearchResult {
  vehicle: Vehicle
  package: TransporterPackage
}

export function filterTransporterPackagesByTerm(
  packages: TransporterPackage[],
  vehicles: Vehicle[],
  searchTerm: string,
): TransporterPackageSearchResult[] {
  if (!searchTerm || searchTerm.trim().length < 2) {
    return []
  }

  const trimmedTerm = searchTerm.trim().toUpperCase()
  const matchingVehicles = vehicles.filter((vehicle) => {
    const vin = vehicle.vin?.toUpperCase() || ''
    const stockNumber = vehicle.stock_number?.toUpperCase() || ''
    return vin.includes(trimmedTerm) || stockNumber.includes(trimmedTerm)
  })

  const results: TransporterPackageSearchResult[] = []
  matchingVehicles.forEach((vehicle) => {
    packages.forEach((pkg) => {
      if (pkg.vehicle_ids && pkg.vehicle_ids.includes(vehicle.id)) {
        results.push({ vehicle, package: pkg })
      }
    })
  })

  return capResults(results, 16)
}

export const useTransporterPackageSearch = ({
  searchTerm,
  enabled = true,
}: UseTransporterPackageSearchOptions) => {
  const { data: packages = [], isPending: packagesPending } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.packages,
    queryFn: fetchTransporterPackages,
    enabled,
    staleTime: SEARCH_CACHE_STALE_MS,
  })
  const { data: vehicles = [], isPending: vehiclesPending } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.inventory,
    queryFn: fetchMasterInventory,
    enabled,
    staleTime: SEARCH_CACHE_STALE_MS,
  })

  const data = useMemo(
    () => filterTransporterPackagesByTerm(packages, vehicles, searchTerm),
    [packages, vehicles, searchTerm],
  )

  return {
    data,
    isLoading:
      (packagesPending && packages.length === 0) || (vehiclesPending && vehicles.length === 0),
  }
}
